import { PulseRootConfigurationToken } from "../providers/tokens.js";
import { isBrowser, isLocalStorageSupported } from "../utils/index.js";
import { PulseInjector } from "./injector.js";
import { ProviderBuilder } from "./provider-builder.js";
export class PulseModule {
    imports = [];
    builder = new ProviderBuilder();
    beforeInitHandler;
    initHandler;
    injector = new PulseInjector();
    parent;
    bootstrapped = false;
    import(m) {
        if (m.bootstrapped) {
            throw new Error(`Cannot import already bootstrapped module! Only root module must be bootstrapped!`);
        }
        m.parent = this;
        this.imports.push(m);
        return this;
    }
    provide(builder) {
        const result = builder(this.builder);
        if (!(result && result instanceof ProviderBuilder)) {
            throw new Error('Provide function must return the received ProviderBuilder instance!');
        }
        this.builder = result;
        return this;
    }
    beforeInit(handler) {
        if (typeof handler !== 'function') {
            throw new Error('BeforeInit handler must be a function!');
        }
        this.beforeInitHandler = handler;
        return this;
    }
    onInit(handler, deps) {
        if (typeof handler !== 'function') {
            throw new Error('OnInit handler must be a function!');
        }
        if (deps && !Array.isArray(deps)) {
            throw new Error('OnInit handler dependency list must be an array!');
        }
        this.initHandler = {
            provide: 'initializer',
            useFactory: handler,
            deps: deps || undefined,
        };
        return this;
    }
    bootstrapStrict(config) {
        return this.bootstrap(config);
    }
    async bootstrap(userConfig) {
        if (this.bootstrapped) {
            throw new Error('PulseModule is already bootstrapped!');
        }
        if (this.bootstrapped) {
            throw new Error('Cannot bootstrap child module!');
        }
        if (this.imports.some((m) => m.bootstrapped)) {
            throw new Error(`Cannot import already bootstrapped module! Only root module must be bootstrapped!`);
        }
        const config = await this.runModulesBeforeInit(userConfig);
        const configOverride = this.getConfigOverride(config);
        const configProvider = {
            provide: PulseRootConfigurationToken,
            useValue: configOverride,
        };
        this.injector.register(configProvider);
        await this.initializeModules(userConfig);
        return this.injector;
    }
    getConfigOverride(config) {
        try {
            // Allows overriding configuration through localStorage.
            // Designed to be used in QA automation to set transport configurations.
            if (isBrowser() && typeof window !== 'undefined' && isLocalStorageSupported(window)) {
                const configString = window['localStorage'].getItem(`${config['storagePrefix']}config_override`);
                if (configString) {
                    const configOverride = JSON.parse(configString);
                    if (!configOverride || typeof configOverride !== 'object') {
                        console.error('Configuration entry must be an object!');
                        return config;
                    }
                    return { ...config, ...configOverride };
                }
            }
        }
        catch (ex) {
            console.error('Pulse configuration provided through localStorage is invalid!');
            console.log(ex);
        }
        return config;
    }
    async initializeModules(config) {
        const modules = [...this.imports, this];
        for (const m of modules) {
            m.configureInjector();
        }
        const initTasks = modules.map((m) => m.runOnInit(config));
        await Promise.all(initTasks);
    }
    configureInjector() {
        if (this.parent && this.imports.length) {
            throw new Error('Deep module imports are not supported yet!');
        }
        const parentInjector = this.parent?.injector;
        const injector = parentInjector ? new PulseInjector(parentInjector) : this.injector;
        const providers = this.builder._getProviders();
        const rootProviders = providers.filter((p) => p.provideInRoot !== false);
        const selfProviders = providers.filter((p) => !p.provideInRoot);
        injector.register(...selfProviders);
        if (parentInjector) {
            parentInjector.register(...rootProviders);
        }
        else if (rootProviders.length) {
            injector.register(...rootProviders);
        }
        this.injector = injector;
    }
    async runModulesBeforeInit(userConfig) {
        const config = await this.runBeforeInit(userConfig);
        const tasks = this.imports.map((m) => m.runBeforeInit(userConfig));
        const moduleConfigs = await Promise.all(tasks);
        return { ...config, ...moduleConfigs.reduce((a, b) => ({ ...a, ...b })) };
    }
    async runBeforeInit(config) {
        try {
            const returnValue = this.beforeInitHandler ? this.beforeInitHandler(config) : undefined;
            const result = returnValue instanceof Promise ? await returnValue : returnValue;
            return result || {};
        }
        catch (ex) {
            console.error('Module beforeInit hook failed!');
            throw ex;
        }
    }
    async runOnInit(config) {
        try {
            if (!this.initHandler?.useFactory) {
                return;
            }
            const dependencyTasks = (this.initHandler.deps || []).map((d) => this.injector.get(d));
            const dependencies = await Promise.all(dependencyTasks);
            const returnValue = this.initHandler.useFactory(config, ...dependencies);
            if (returnValue instanceof Promise) {
                await returnValue;
            }
        }
        catch (ex) {
            console.error(ex);
            console.error('Module onInit hook failed!');
            throw ex;
        }
    }
}
//# sourceMappingURL=module.js.map